<template>
    <div class="report-page">
        <!-- 顶部装饰背景 -->
        <div class="report-header-bg">
            <div class="header-circle c1"></div>
            <div class="header-circle c2"></div>
            <div class="header-circle c3"></div>
        </div>

        <!-- 导航栏 -->
        <div class="report-nav">
            <div class="nav-back" @click="goBack">
                <div class="back-btn-circle">
                    <span class="back-icon">‹</span>
                </div>
            </div>
            <div class="nav-title-wrap">
                <span class="nav-title">{{ reportTitle }}</span>
                <div class="nav-title-line"></div>
            </div>
            <div class="nav-placeholder"></div>
        </div>

        <!-- 加载状态 -->
        <div class="report-loading" v-if="loading">
            <div class="loading-animation">
                <div
                    class="loading-dot"
                    v-for="i in 3"
                    :key="i"
                    :style="{ animationDelay: i * 0.15 + 's' }"
                ></div>
            </div>
            <span class="loading-text">小酒窝正在为你生成报告...</span>
        </div>

        <!-- 报告内容 -->
        <div class="report-scroll" v-else-if="hasReport">
            <div class="report-card">
                <!-- 卡片头部 -->
                <div class="card-header">
                    <div class="card-badge">📋</div>
                    <div class="card-title-wrap">
                        <span class="card-title">{{ reportTitle }}</span>
                        <span class="card-date">{{ reportDate }}</span>
                    </div>
                </div>
                <!-- 分割线 -->
                <div class="card-divider">
                    <div class="divider-line"></div>
                    <div class="divider-dot"></div>
                    <div class="divider-line"></div>
                </div>

                <!-- 纯文本降级展示（报告非 JSON 时） -->
                <div class="card-body" v-if="!reportData && reportContent">
                    <span class="report-text">{{ reportContent }}</span>
                </div>

                <!-- 结构化报告展示（reportData 存在时，按 docs/报告json.md、docs/评估报告.md 规则排版） -->
                <div class="card-body" v-else-if="reportData">
                    <!-- 评估结论（仅评估报告，severity 困扰程度徽章） -->
                    <div class="rp-section" v-if="reportData.severity">
                        <div class="rp-section-title">
                            <span class="rp-title-icon">📌</span>
                            <span class="rp-title-text">评估结论</span>
                        </div>
                        <span class="rp-risk-result" :class="severityClass">
                            {{ reportData.severity }}
                        </span>
                    </div>

                    <!-- 咨询概要（仅评估报告：概要 + 主诉 + 关键问题标签） -->
                    <div class="rp-section" v-if="reportData.consultationReview">
                        <div class="rp-section-title">
                            <span class="rp-title-icon">📖</span>
                            <span class="rp-title-text">咨询概要</span>
                        </div>
                        <div
                            class="rp-topic"
                            v-if="reportData.consultationReview.consultationSummary"
                        >
                            {{ reportData.consultationReview.consultationSummary }}
                        </div>
                        <div
                            class="rp-plan-item"
                            style="margin-top: 16rpx"
                            v-if="reportData.consultationReview.mainComplaint"
                        >
                            <span class="rp-plan-label">主诉</span>
                            <span class="rp-plan-text">
                                {{ reportData.consultationReview.mainComplaint }}
                            </span>
                        </div>
                        <div class="rp-tags" v-if="keyIssuesList.length > 0">
                            <span
                                class="rp-tag"
                                v-for="(issue, i) in keyIssuesList"
                                :key="'issue' + i"
                            >
                                {{ issue }}
                            </span>
                        </div>
                    </div>

                    <!-- 信息采集度（仅评估报告，5维度完整度进度条） -->
                    <div class="rp-section" v-if="infoCollectionList.length > 0">
                        <div class="rp-section-title">
                            <span class="rp-title-icon">📥</span>
                            <span class="rp-title-text">信息采集度</span>
                        </div>
                        <div
                            class="rp-emotion"
                            v-for="item in infoCollectionList"
                            :key="'ic' + item.key"
                        >
                            <div class="rp-emotion-head">
                                <span class="rp-emotion-name">{{ item.label }}</span>
                                <span class="rp-emotion-score">
                                    {{ item.value }}
                                    <span class="rp-score-max">/100</span>
                                </span>
                            </div>
                            <div class="rp-score-bar">
                                <div
                                    class="rp-score-fill"
                                    :style="{ width: item.value + '%' }"
                                ></div>
                            </div>
                        </div>
                        <div class="rp-risk-desc" v-if="reportData.collectionCompleteness">
                            {{ reportData.collectionCompleteness }}
                        </div>
                    </div>

                    <!-- 采集明细（仅评估报告，24项问答归档） -->
                    <div class="rp-section" v-if="responseGroups.length > 0">
                        <div class="rp-section-title">
                            <span class="rp-title-icon">🗂️</span>
                            <span class="rp-title-text">采集明细</span>
                        </div>
                        <div class="rp-group" v-for="group in responseGroups" :key="group.title">
                            <div class="rp-group-title">{{ group.title }}</div>
                            <div
                                class="rp-plan-item"
                                v-for="item in group.items"
                                :key="group.title + item.name"
                            >
                                <span class="rp-plan-label">{{ item.name }}</span>
                                <span
                                    class="rp-plan-text"
                                    :class="{ unknown: item.value === '未知' }"
                                >
                                    {{ item.value }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- 会话主题 -->
                    <div class="rp-section" v-if="reportData.sessionTopic">
                        <div class="rp-section-title">
                            <span class="rp-title-icon">🎯</span>
                            <span class="rp-title-text">会话主题</span>
                        </div>
                        <div class="rp-topic">{{ reportData.sessionTopic || '未识别' }}</div>
                    </div>

                    <!-- 敏感词标签 -->
                    <div class="rp-section" v-if="sensitiveWordsList.length > 0">
                        <div class="rp-section-title">
                            <span class="rp-title-icon">🏷️</span>
                            <span class="rp-title-text">敏感词</span>
                        </div>
                        <div class="rp-tags">
                            <span class="rp-tag" v-for="(w, i) in sensitiveWordsList" :key="i">
                                {{ w }}
                            </span>
                        </div>
                    </div>

                    <!-- 高危预警 -->
                    <div class="rp-section">
                        <div class="rp-section-title">
                            <span class="rp-title-icon">⚠️</span>
                            <span class="rp-title-text">高危预警</span>
                        </div>
                        <div class="rp-alert" :class="isSafe ? 'safe' : 'risk'">
                            {{ highRiskText }}
                        </div>
                    </div>

                    <!-- 情绪特征评分 -->
                    <div class="rp-section" v-if="emotionalTraitsList.length > 0">
                        <div class="rp-section-title">
                            <span class="rp-title-icon">🌡️</span>
                            <span class="rp-title-text">情绪特征</span>
                        </div>
                        <div
                            class="rp-emotion"
                            v-for="trait in emotionalTraitsList"
                            :key="trait.key"
                        >
                            <div class="rp-emotion-head">
                                <span class="rp-emotion-name">{{ trait.label }}</span>
                                <span class="rp-emotion-score">
                                    {{ trait.currentScore }}
                                    <span class="rp-score-max">/{{ trait.maxScore }}</span>
                                </span>
                            </div>
                            <!-- 评分条（宽高用内联，避免 gap 兼容问题） -->
                            <div class="rp-score-bar">
                                <div
                                    class="rp-score-fill"
                                    :style="{ width: scorePercent(trait) }"
                                ></div>
                            </div>
                            <div class="rp-score-range" v-if="trait.scoringHint">
                                {{ trait.scoringHint }}
                            </div>
                            <!-- 用户原话引用 -->
                            <div
                                class="rp-quote"
                                v-if="trait.quote && trait.quote !== '无相关原话'"
                            >
                                <span class="rp-quote-label">原话</span>
                                <span class="rp-quote-text">{{ trait.quote }}</span>
                            </div>
                            <!-- 建议 -->
                            <div class="rp-suggestion" v-if="trait.suggestion">
                                <span class="rp-suggestion-label">建议</span>
                                <span class="rp-suggestion-text">{{ trait.suggestion }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- 维度评估（仅评估报告，level/percent 与情绪特征评分联动） -->
                    <div class="rp-section" v-if="dimensionList.length > 0">
                        <div class="rp-section-title">
                            <span class="rp-title-icon">📊</span>
                            <span class="rp-title-text">维度评估</span>
                        </div>
                        <div class="rp-emotion" v-for="dim in dimensionList" :key="'dim' + dim.key">
                            <div class="rp-emotion-head">
                                <span class="rp-emotion-name">{{ dim.label }}</span>
                                <span class="rp-dim-level" :class="dim.levelClass">
                                    {{ dim.level }}
                                </span>
                            </div>
                            <div class="rp-score-bar">
                                <div
                                    class="rp-score-fill"
                                    :style="{ width: dim.percent + '%' }"
                                ></div>
                            </div>
                            <div class="rp-risk-desc" v-if="dim.description">
                                {{ dim.description }}
                            </div>
                        </div>
                    </div>

                    <!-- 核心机制（仅评估报告） -->
                    <div class="rp-section" v-if="reportData.conceptualization">
                        <div class="rp-section-title">
                            <span class="rp-title-icon">🧩</span>
                            <span class="rp-title-text">核心机制</span>
                        </div>
                        <div class="rp-conclusion">{{ reportData.conceptualization }}</div>
                    </div>

                    <!-- 心理画像（仅评估报告） -->
                    <div class="rp-section" v-if="reportData.personality_profile">
                        <div class="rp-section-title">
                            <span class="rp-title-icon">👤</span>
                            <span class="rp-title-text">心理画像</span>
                        </div>
                        <div class="rp-conclusion">{{ reportData.personality_profile }}</div>
                    </div>

                    <!-- 诊断参考（仅评估报告） -->
                    <div class="rp-section" v-if="reportData.diagnosis_reference">
                        <div class="rp-section-title">
                            <span class="rp-title-icon">🩺</span>
                            <span class="rp-title-text">诊断参考</span>
                        </div>
                        <div class="rp-conclusion">{{ reportData.diagnosis_reference }}</div>
                    </div>

                    <!-- 风险评估 -->
                    <div class="rp-section" v-if="reportData.riskAssessment">
                        <div class="rp-section-title">
                            <span class="rp-title-icon">🚦</span>
                            <span class="rp-title-text">风险评估</span>
                        </div>
                        <span class="rp-risk-result" :class="riskLevelClass">
                            {{ riskResultText }}
                        </span>
                        <!-- 高风险问题说明（评估报告 highRiskExistence） -->
                        <div class="rp-plan-item" v-if="highRiskExistenceText">
                            <span class="rp-plan-label">高风险问题</span>
                            <span class="rp-plan-text">{{ highRiskExistenceText }}</span>
                        </div>
                        <div class="rp-risk-desc" v-if="reportData.riskAssessment.potentialRisk">
                            {{ reportData.riskAssessment.potentialRisk }}
                        </div>
                    </div>

                    <!-- 干预方案 -->
                    <div class="rp-section" v-if="reportData.interventionPlan">
                        <div class="rp-section-title">
                            <span class="rp-title-icon">🧭</span>
                            <span class="rp-title-text">干预方案</span>
                        </div>
                        <div
                            class="rp-plan-item"
                            v-if="reportData.interventionPlan.psychologicalCounseling"
                        >
                            <span class="rp-plan-label">心理干预</span>
                            <span class="rp-plan-text">
                                {{ reportData.interventionPlan.psychologicalCounseling }}
                            </span>
                        </div>
                        <!-- 家校支持区块按需求隐藏，不做渲染 -->
                        <div
                            class="rp-plan-item"
                            v-if="reportData.interventionPlan.otherSuggestions"
                        >
                            <span class="rp-plan-label">补充建议</span>
                            <span class="rp-plan-text">
                                {{ reportData.interventionPlan.otherSuggestions }}
                            </span>
                        </div>
                    </div>

                    <!-- 针对性建议（仅评估报告） -->
                    <div class="rp-section" v-if="targetedSuggestionList.length > 0">
                        <div class="rp-section-title">
                            <span class="rp-title-icon">💡</span>
                            <span class="rp-title-text">针对性建议</span>
                        </div>
                        <div
                            class="rp-plan-item"
                            v-for="(s, i) in targetedSuggestionList"
                            :key="'ts' + i"
                        >
                            <span class="rp-plan-label">{{ s.title }}</span>
                            <span class="rp-plan-text">{{ s.description }}</span>
                        </div>
                    </div>

                    <!-- 咨询建议（仅评估报告） -->
                    <div class="rp-section" v-if="counselingList.length > 0">
                        <div class="rp-section-title">
                            <span class="rp-title-icon">🧭</span>
                            <span class="rp-title-text">咨询建议</span>
                        </div>
                        <div
                            class="rp-plan-item"
                            v-for="item in counselingList"
                            :key="'cs' + item.label"
                        >
                            <span class="rp-plan-label">{{ item.label }}</span>
                            <span class="rp-plan-text">{{ item.value }}</span>
                        </div>
                    </div>

                    <!-- 综合评估 -->
                    <div class="rp-section" v-if="reportData.comprehensiveEvaluation">
                        <div class="rp-section-title">
                            <span class="rp-title-icon">📝</span>
                            <span class="rp-title-text">综合评估</span>
                        </div>
                        <div class="rp-conclusion">{{ reportData.comprehensiveEvaluation }}</div>
                    </div>
                </div>

                <!-- 卡片底部 -->
                <div class="card-footer">
                    <div class="footer-line"></div>
                    <span class="footer-text">由小酒窝 AI 生成 · 仅供参考</span>
                </div>
            </div>

            <!-- 底部返回按钮 -->
            <div class="report-bottom">
                <div class="back-home-btn" @click="goBack">
                    <span class="back-home-text">返回首页</span>
                </div>
            </div>
        </div>

        <!-- 生成失败 -->
        <div class="report-error" v-else-if="errorMsg">
            <span class="error-icon">😞</span>
            <span class="error-title">报告生成失败</span>
            <span class="error-desc">{{ errorMsg }}</span>
            <div class="retry-btn" @click="generateReport">
                <span class="retry-text">重新生成</span>
            </div>
        </div>
    </div>
</template>

<script>
import { request } from '@/utils/request.js';

// 情绪特征展示顺序（对应 docs/报告json.md Schema）
const EMOTION_KEYS = ['anxiety', 'obsession', 'depression', 'hostilityViolence', 'selfHarm'];
// 情绪特征中文兜底名（报告缺 label 时使用）
const EMOTION_LABELS = {
    anxiety: '焦虑',
    obsession: '强迫',
    depression: '抑郁',
    hostilityViolence: '敌对暴力',
    selfHarm: '自我伤害',
};
// 风险等级样式（is_warning：0正常/1低/2中/3高）
const RISK_CLASS = ['risk-lv-normal', 'risk-lv-low', 'risk-lv-mid', 'risk-lv-high'];

export default {
    data() {
        return {
            conversationId: '', // 会话ID
            isAssessment: false, // 是否为评估报告（路由 query type=assessment）
            loading: true, // 是否加载中
            reportData: null, // 结构化报告 JSON 对象（按 docs/报告json.md 规则）
            reportContent: '', // 降级纯文本内容（报告非 JSON 时）
            reportDate: '', // 报告日期
            errorMsg: '', // 错误信息
        };
    },
    computed: {
        // 报告标题（评估模式与普通咨询模式区分）
        reportTitle() {
            return this.isAssessment ? '心理评估报告' : '心理咨询总结报告';
        },
        // 是否有可展示的报告（结构化对象或纯文本任一存在）
        hasReport() {
            return !!this.reportData || !!this.reportContent;
        },
        // 心理困扰程度样式 class（severity 四档 → 复用风险配色）
        severityClass() {
            const map = { 无困扰: 0, 轻度困扰: 1, 中度困扰: 2, 重度困扰: 3 };
            const idx = map[this.reportData && this.reportData.severity];
            return RISK_CLASS[idx === undefined ? 0 : idx];
        },
        // 关键问题标签列表（评估报告 keyIssues 数组）
        keyIssuesList() {
            const issues =
                (this.reportData &&
                    this.reportData.consultationReview &&
                    this.reportData.consultationReview.keyIssues) ||
                [];
            return Array.isArray(issues) ? issues.filter(Boolean) : [];
        },
        // 信息采集度列表（评估报告 infoCollection 5维度，数值钳制 0-100）
        infoCollectionList() {
            const source = (this.reportData && this.reportData.infoCollection) || {};
            const meta = [
                ['basicInfo', '基础信息'],
                ['processComplaint', '过程主诉'],
                ['personalBiological', '个人生物'],
                ['contextualSocial', '情境社会'],
                ['historicalPast', '历史既往'],
            ];
            return meta
                .filter(
                    ([key]) =>
                        source[key] !== undefined && source[key] !== null && source[key] !== ''
                )
                .map(([key, label]) => ({
                    key,
                    label,
                    value: Math.max(0, Math.min(100, Number(source[key]) || 0)),
                }));
        },
        // 采集明细分组（评估报告 responses 4组24项，key 形如 "1_具体表现"）
        responseGroups() {
            const source = (this.reportData && this.reportData.responses) || {};
            const groupMeta = [
                ['process', '过程-主诉'],
                ['personal', '个人-生物症状'],
                ['contextual', '情境-社会情况'],
                ['historical', '历史-既往情况'],
            ];
            return groupMeta
                .filter(([, key]) => source[key] && typeof source[key] === 'object')
                .map(([key, title]) => ({
                    title,
                    items: Object.entries(source[key]).map(([field, value]) => {
                        // 字段 key 形如 "1_具体表现"，取编号后的名称展示
                        const name = field.includes('_')
                            ? field.split('_').slice(1).join(' ')
                            : field;
                        return { name, value: String(value || '未知') };
                    }),
                }))
                .filter((g) => g.items.length > 0);
        },
        // 维度评估列表（评估报告 dimensionAssessment，与情绪特征同顺序）
        dimensionList() {
            const source = (this.reportData && this.reportData.dimensionAssessment) || {};
            // level 中文 → 4 档配色 class
            const levelClassMap = {
                正常: 'risk-lv-normal',
                轻度: 'risk-lv-low',
                中度: 'risk-lv-mid',
                重度: 'risk-lv-high',
            };
            return EMOTION_KEYS.filter((k) => source[k]).map((k) => {
                const d = source[k] || {};
                return {
                    key: k,
                    label: EMOTION_LABELS[k],
                    level: d.level || '未评估',
                    description: d.description || '',
                    percent: Math.max(0, Math.min(100, Number(d.percent) || 0)),
                    levelClass: levelClassMap[d.level] || 'risk-lv-normal',
                };
            });
        },
        // 针对性建议列表（评估报告 targetedSuggestions 数组）
        targetedSuggestionList() {
            const list = (this.reportData && this.reportData.targetedSuggestions) || [];
            return Array.isArray(list) ? list.filter((s) => s && (s.title || s.description)) : [];
        },
        // 咨询建议列表（评估报告 counseling_suggestion 5项，过滤空值与"无"）
        counselingList() {
            const c = (this.reportData && this.reportData.counseling_suggestion) || {};
            const meta = [
                ['is_recommended', '介入建议'],
                ['modality_frequency', '方式频率'],
                ['selfCareAdvice', '自我调理'],
                ['referral_suggestion', '转介建议'],
                ['initial_goals', '改善目标'],
            ];
            return meta
                .filter(([key]) => c[key] && c[key] !== '无')
                .map(([key, label]) => ({ label, value: c[key] }));
        },
        // 高风险问题说明（评估报告 riskAssessment.highRiskExistence，"是/否 + 说明"）
        highRiskExistenceText() {
            const h =
                this.reportData &&
                this.reportData.riskAssessment &&
                this.reportData.riskAssessment.highRiskExistence;
            if (!h || !h.label) return '';
            return h.description ? `${h.label}，${h.description}` : h.label;
        },
        // 情绪特征列表（补全中文名，过滤无效项，保持固定顺序）
        emotionalTraitsList() {
            if (!this.reportData || !this.reportData.emotionalTraits) return [];
            return EMOTION_KEYS.map((k) => {
                const t = this.reportData.emotionalTraits[k] || {};
                return { key: k, ...t, label: t.label || EMOTION_LABELS[k] };
            }).filter((t) => t.label);
        },
        // 敏感词列表（按顿号/逗号/空格拆分为标签，剔除"无"）
        sensitiveWordsList() {
            const words = (this.reportData && this.reportData.sensitiveWords) || '';
            return words.split(/[、，,\s]+/).filter((w) => w && w !== '无');
        },
        // 高危预警内容（普通报告取高危语句记录，评估报告取高危信号字段，默认安全话术）
        highRiskText() {
            const d = this.reportData || {};
            if (d.highRiskStatementRecord && d.highRiskStatementRecord.content) {
                return d.highRiskStatementRecord.content;
            }
            // 评估报告：high_risk_signals 为 "无" 或缺失视为安全
            if (d.high_risk_signals && d.high_risk_signals !== '无') {
                return d.high_risk_signals;
            }
            return '整场会话未发现明显高危预警语句';
        },
        // 是否视为安全（命中默认安全话术即判定安全）
        isSafe() {
            const t = this.highRiskText;
            return /未发现明显高危|无高危|安全$/.test(t) || t === '整场会话未发现明显高危预警语句';
        },
        // 风险评估文案
        riskResultText() {
            const r =
                (this.reportData &&
                    this.reportData.riskAssessment &&
                    this.reportData.riskAssessment.result) ||
                '未评估';
            return r;
        },
        // 风险等级样式 class（按 is_warning 归一化到 0~3）
        riskLevelClass() {
            const w =
                (this.reportData &&
                    this.reportData.riskAssessment &&
                    this.reportData.riskAssessment.is_warning) ||
                0;
            const num = Math.max(0, Math.min(3, Number(w) || 0));
            return RISK_CLASS[num];
        },
    },
    created() {
        // 从路由 query 中获取会话ID（对应 uniapp 的 onLoad(options)）
        this.conversationId = this.$route.query.conversationId || '';
        // type=assessment 表示评估报告，后端用 generate_report intent 生成
        this.isAssessment = this.$route.query.type === 'assessment';
        this.reportDate = this.formatDate(new Date());
        if (this.conversationId) {
            this.generateReport();
        } else {
            this.loading = false;
            this.errorMsg = '缺少会话信息';
        }
    },
    methods: {
        // 调用后端生成报告（普通咨询报告/评估报告共用本页）
        async generateReport() {
            this.loading = true;
            this.errorMsg = '';
            this.reportData = null;
            this.reportContent = '';
            try {
                const res = await request({
                    url: '/api/generate-report',
                    method: 'POST',
                    data: { conversationId: this.conversationId, isAssessment: this.isAssessment },
                });
                if (res.data.code === 200) {
                    const data = res.data.data || {};
                    // 优先使用结构化 report 对象，无则降级为文本 content
                    this.reportData = data.report || null;
                    this.reportContent = data.content || '';
                    if (!this.reportData && !this.reportContent) {
                        this.errorMsg = '报告内容为空';
                    }
                } else {
                    this.errorMsg = res.data.message || '生成失败';
                }
            } catch (e) {
                console.error('生成报告失败:', e);
                // 401 踢下线场景：request.js 已统一弹提示并跳登录页，此处不再展示错误
                if (e.isAuthError) return;
                this.errorMsg = '网络异常，请重试';
            } finally {
                this.loading = false;
            }
        },
        // 计算评分百分比（当前分/满分，用于进度条宽度）
        scorePercent(trait) {
            const max = Number(trait.maxScore) || 6;
            const cur = Number(trait.currentScore) || 0;
            const pct = (cur / max) * 100;
            return `${Math.max(0, Math.min(100, pct)).toFixed(0)}%`;
        },
        // 返回上一页或首页
        goBack() {
            // 有历史记录则返回上一页，否则跳转聊天首页（对应 uniapp 页面栈判断）
            if (window.history.length > 1) {
                this.$router.back();
            } else {
                this.$router.replace('/chat');
            }
        },
        // 格式化日期
        formatDate(date) {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            const h = String(date.getHours()).padStart(2, '0');
            const min = String(date.getMinutes()).padStart(2, '0');
            return `${y}-${m}-${d} ${h}:${min}`;
        },
    },
};
</script>

<style lang="scss" scoped>
.report-page {
    min-height: 100vh;
    background: linear-gradient(180deg, #fff5ee 0%, #fef6f0 40%, #f8f4f2 100%);
    position: relative;
    overflow: hidden;
}

// 顶部装饰圆
.report-header-bg {
    position: absolute;
    top: -60rpx;
    right: -40rpx;
    z-index: 0;
}
.header-circle {
    position: absolute;
    border-radius: 50%;
    opacity: 0.15;
    &.c1 {
        width: 240rpx;
        height: 240rpx;
        background: #e8735a;
        top: 0;
        right: 0;
    }
    &.c2 {
        width: 160rpx;
        height: 160rpx;
        background: #f4a261;
        top: 120rpx;
        right: -60rpx;
    }
    &.c3 {
        width: 100rpx;
        height: 100rpx;
        background: #c0513f;
        top: 40rpx;
        right: 100rpx;
    }
}

// 导航栏
.report-nav {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 80rpx 32rpx 24rpx;
}

// 返回按钮
.nav-back {
    width: 120rpx;
    display: flex;
    align-items: center;
    cursor: pointer;
    &:active {
        opacity: 0.7;
    }
}
.back-btn-circle {
    width: 72rpx;
    height: 72rpx;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4rpx 16rpx rgba(0, 0, 0, 0.08);
    border: 1rpx solid rgba(192, 81, 63, 0.1);
    transition: all 0.2s ease;
    &:active {
        transform: scale(0.92);
        background: rgba(255, 255, 255, 1);
    }
}
.back-icon {
    font-size: 70rpx;
    color: #5a3a3a;
    font-weight: 300;
    line-height: 1;
    position: relative;
    top: -6rpx;
    left: -4rpx;
}

// 标题区域
.nav-title-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
}
.nav-title {
    font-size: 36rpx;
    font-weight: 700;
    color: #3d2c2c;
    letter-spacing: 4rpx;
}
.nav-title-line {
    width: 48rpx;
    height: 4rpx;
    background: linear-gradient(90deg, transparent, #e8735a, transparent);
    border-radius: 2rpx;
    margin-top: 8rpx;
}
.nav-placeholder {
    width: 120rpx;
}

// 加载状态
.report-loading {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-top: 200rpx;
}
// 加载动画（gap 改为相邻 margin，兼容旧浏览器）
.loading-animation {
    display: flex;
    margin-bottom: 32rpx;
}
.loading-dot {
    width: 20rpx;
    height: 20rpx;
    border-radius: 50%;
    background: linear-gradient(135deg, #e8735a, #f4a261);
    animation: dotBounce 1.2s ease-in-out infinite;
}
.loading-dot + .loading-dot {
    margin-left: 16rpx;
}
@keyframes dotBounce {
    0%,
    80%,
    100% {
        transform: scale(0.6);
        opacity: 0.4;
    }
    40% {
        transform: scale(1);
        opacity: 1;
    }
}
.loading-text {
    font-size: 28rpx;
    color: #a08070;
}

// 报告滚动区（scroll-view 改为普通 div 滚动）
.report-scroll {
    position: relative;
    z-index: 1;
    height: calc(100vh - 140rpx);
    padding: 20rpx 32rpx 40rpx;
    box-sizing: border-box;
    overflow-y: auto;
}

// 报告卡片
.report-card {
    background: #fff;
    border-radius: 24rpx;
    padding: 40rpx 36rpx;
    box-shadow: 0 8rpx 32rpx rgba(0, 0, 0, 0.06);
    border: 1rpx solid rgba(192, 81, 63, 0.06);
}
.card-header {
    display: flex;
    align-items: center;
    margin-bottom: 24rpx;
}
.card-badge {
    font-size: 48rpx;
    margin-right: 20rpx;
}
.card-title-wrap {
    display: flex;
    flex-direction: column;
}
.card-title {
    font-size: 32rpx;
    font-weight: 700;
    color: #3d2c2c;
}
.card-date {
    font-size: 22rpx;
    color: #b0a098;
    margin-top: 6rpx;
}

// 分割线（装饰性，gap 改为相邻 margin）
.card-divider {
    display: flex;
    align-items: center;
    margin-bottom: 28rpx;
}
.divider-line + .divider-dot,
.divider-dot + .divider-line {
    margin-left: 16rpx;
}
.divider-line {
    flex: 1;
    height: 1rpx;
    background: linear-gradient(90deg, transparent, rgba(192, 81, 63, 0.2), transparent);
}
.divider-dot {
    width: 10rpx;
    height: 10rpx;
    border-radius: 50%;
    background: #e8735a;
    opacity: 0.5;
}

// 报告正文（纯文本降级）
.card-body {
    margin-bottom: 28rpx;
}
.report-text {
    font-size: 28rpx;
    color: #4a3a3a;
    line-height: 2;
    white-space: pre-wrap;
    word-break: break-word;
}

// 卡片底部
.card-footer {
    display: flex;
    flex-direction: column;
    align-items: center;
}
.footer-line {
    width: 80rpx;
    height: 1rpx;
    background: rgba(0, 0, 0, 0.08);
    margin-bottom: 16rpx;
}
.footer-text {
    font-size: 22rpx;
    color: #c0b0a8;
}

// 底部按钮（内部单个子元素，原 gap 直接删除）
.report-bottom {
    display: flex;
    justify-content: center;
    padding: 40rpx 0 60rpx;
}
.back-home-btn {
    padding: 24rpx 72rpx;
    border-radius: 44rpx;
    background: linear-gradient(135deg, #e8735a 0%, #c0513f 100%);
    box-shadow: 0 8rpx 28rpx rgba(232, 115, 90, 0.35), 0 2rpx 8rpx rgba(192, 81, 63, 0.2);
    display: flex;
    align-items: center;
    cursor: pointer;
    transition: all 0.2s ease;
    &:active {
        transform: translateY(2rpx);
        box-shadow: 0 4rpx 16rpx rgba(232, 115, 90, 0.25);
    }
}
.back-home-text {
    font-size: 30rpx;
    color: #fff;
    font-weight: 600;
    letter-spacing: 2rpx;
}

// 错误状态
.report-error {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 200rpx;
}
.error-icon {
    font-size: 80rpx;
    margin-bottom: 24rpx;
}
.error-title {
    font-size: 32rpx;
    font-weight: 600;
    color: #3d2c2c;
    margin-bottom: 12rpx;
}
.error-desc {
    font-size: 26rpx;
    color: #a08070;
    margin-bottom: 40rpx;
}
.retry-btn {
    padding: 18rpx 56rpx;
    border-radius: 32rpx;
    background: rgba(232, 115, 90, 0.1);
    border: 1rpx solid rgba(232, 115, 90, 0.3);
    cursor: pointer;
    &:active {
        opacity: 0.7;
    }
}
.retry-text {
    font-size: 28rpx;
    color: #c0513f;
    font-weight: 600;
}

// ===== 结构化报告区（新增，间距用 margin 兼容旧浏览器，禁止 gap）=====
.rp-section {
    margin-bottom: 32rpx;
}
.rp-section-title {
    display: flex;
    align-items: center;
    margin-bottom: 16rpx;
}
.rp-title-icon {
    font-size: 30rpx;
    margin-right: 10rpx;
}
.rp-title-text {
    font-size: 28rpx;
    font-weight: 700;
    color: #3d2c2c;
}
.rp-topic {
    font-size: 28rpx;
    color: #4a3a3a;
    line-height: 1.8;
    background: #fff7f2;
    border-radius: 16rpx;
    padding: 20rpx 24rpx;
}
.rp-tags {
    display: flex;
    flex-wrap: wrap;
}
.rp-tag {
    font-size: 24rpx;
    color: #c0513f;
    background: rgba(232, 115, 90, 0.12);
    border-radius: 24rpx;
    padding: 6rpx 20rpx;
    margin: 0 12rpx 12rpx 0;
}
.rp-alert {
    font-size: 26rpx;
    line-height: 1.8;
    border-radius: 16rpx;
    padding: 20rpx 24rpx;
    &.safe {
        color: #2f9e6e;
        background: rgba(47, 158, 110, 0.1);
        border: 1rpx solid rgba(47, 158, 110, 0.2);
    }
    &.risk {
        color: #e0472e;
        background: rgba(224, 71, 46, 0.1);
        border: 1rpx solid rgba(224, 71, 46, 0.2);
    }
}
.rp-emotion {
    background: #fff9f5;
    border-radius: 16rpx;
    padding: 24rpx;
    margin-bottom: 20rpx;
}
.rp-emotion-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14rpx;
}
.rp-emotion-name {
    font-size: 28rpx;
    font-weight: 700;
    color: #3d2c2c;
}
.rp-emotion-score {
    font-size: 30rpx;
    font-weight: 700;
    color: #e8735a;
}
.rp-score-max {
    font-size: 24rpx;
    color: #b0a098;
    font-weight: 400;
}
.rp-score-bar {
    height: 12rpx;
    background: #f0e4dd;
    border-radius: 6rpx;
    overflow: hidden;
    margin-bottom: 12rpx;
}
.rp-score-fill {
    height: 100%;
    background: linear-gradient(90deg, #e8735a, #f4a261);
    border-radius: 6rpx;
}
.rp-score-range {
    font-size: 22rpx;
    color: #a08070;
    line-height: 1.6;
    margin-bottom: 14rpx;
}
.rp-quote,
.rp-suggestion {
    display: flex;
    align-items: flex-start;
    margin-top: 10rpx;
}
.rp-quote-label,
.rp-suggestion-label {
    font-size: 22rpx;
    color: #fff;
    flex-shrink: 0;
    border-radius: 8rpx;
    padding: 4rpx 12rpx;
    margin-right: 12rpx;
    margin-top: 2rpx;
}
.rp-quote-label {
    background: #e8735a;
}
.rp-suggestion-label {
    background: #f4a261;
}
.rp-quote-text,
.rp-suggestion-text {
    font-size: 24rpx;
    color: #6a5a55;
    line-height: 1.7;
    word-break: break-word;
}
.rp-risk-result {
    display: inline-block;
    font-size: 26rpx;
    font-weight: 700;
    border-radius: 24rpx;
    padding: 8rpx 24rpx;
    margin-bottom: 12rpx;
}
.risk-lv-normal {
    color: #2f9e6e;
    background: rgba(47, 158, 110, 0.12);
}
.risk-lv-low {
    color: #c8932a;
    background: rgba(200, 147, 42, 0.12);
}
.risk-lv-mid {
    color: #e07a2e;
    background: rgba(224, 122, 46, 0.12);
}
.risk-lv-high {
    color: #e0472e;
    background: rgba(224, 71, 46, 0.12);
}
.rp-risk-desc {
    font-size: 26rpx;
    color: #4a3a3a;
    line-height: 1.8;
}
.rp-plan-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 16rpx;
    &:last-child {
        margin-bottom: 0;
    }
}
.rp-plan-label {
    flex-shrink: 0;
    font-size: 24rpx;
    font-weight: 600;
    color: #c0513f;
    background: rgba(232, 115, 90, 0.1);
    border-radius: 8rpx;
    padding: 4rpx 12rpx;
    margin: 2rpx 12rpx 0 0;
}
.rp-plan-text {
    font-size: 26rpx;
    color: #4a3a3a;
    line-height: 1.7;
    word-break: break-word;
    &.unknown {
        color: #c0b0a8; // 采集明细中"未知"项置灰
    }
}
// 维度评估等级标签（配合 risk-lv-* 四档配色使用）
.rp-dim-level {
    font-size: 24rpx;
    font-weight: 700;
    border-radius: 20rpx;
    padding: 4rpx 16rpx;
}
// 采集明细分组
.rp-group {
    margin-bottom: 20rpx;
}
.rp-group-title {
    font-size: 26rpx;
    font-weight: 700;
    color: #c0513f;
    margin-bottom: 12rpx;
}
.rp-conclusion {
    font-size: 26rpx;
    color: #4a3a3a;
    line-height: 1.9;
    background: #fff7f2;
    border-radius: 16rpx;
    padding: 20rpx 24rpx;
}
</style>
